import { memo, useCallback, useMemo, useState, useSyncExternalStore } from 'react'
import {
    Box,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover
} from '@mui/material'
import { ExtraSmallButton } from '../../components/custom'

export type OiAnalysisExpiryOption = {
    value: string
    label: string
}

const EXPIRY_BUTTON_MAX_WIDTH_PX = 280

const subscribeFullscreen = (onStoreChange: () => void) => {
    document.addEventListener('fullscreenchange', onStoreChange)
    return () => document.removeEventListener('fullscreenchange', onStoreChange)
}

const getFullscreenContainer = () => document.fullscreenElement as HTMLElement | null

const useFullscreenContainer = () =>
    useSyncExternalStore(subscribeFullscreen, getFullscreenContainer, () => null)

type Props = {
    options: OiAnalysisExpiryOption[]
    selected: string[]
    onChange: (next: string[]) => void
    loading: boolean
    idPrefix: string
    compact?: boolean
}

const OiAnalysisExpiriesMenu = memo(({ options, selected, onChange, loading, idPrefix, compact = false }: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const fullscreenContainer = useFullscreenContainer()
    const open = Boolean(anchorEl)

    const buttonLabel = useMemo(() => {
        if (selected.length === 0) {
            return 'Expiry'
        }
        return selected
            .map((v) => {
                const opt = options.find((o) => o.value === v)
                return opt?.label ?? v
            })
            .join(', ')
    }, [selected, options])

    const toggle = useCallback(
        (value: string) => {
            const isSelected = selected.includes(value)
            let next: string[]
            if (isSelected) {
                next = selected.filter((v) => v !== value)
                if (next.length === 0) {
                    return
                }
            } else {
                next = [...selected, value]
            }
            onChange(next)
        },
        [selected, onChange]
    )

    const disabled = loading || options.length === 0

    if (!loading && options.length === 0) {
        return null
    }

    return (
        <>
            <ExtraSmallButton
                color="inherit"
                id={`${idPrefix}-expiry-menu-button`}
                variant="outlined"
                disabled={disabled}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                title={buttonLabel}
                sx={{
                    height: compact ? '24px' : '32px',
                    fontSize: compact ? '13px' : undefined,
                    boxSizing: 'border-box',
                    width: 'fit-content',
                    maxWidth: compact ? '140px' : `${EXPIRY_BUTTON_MAX_WIDTH_PX}px`,
                    alignSelf: 'flex-start',
                    borderColor: (theme) => theme.palette.background.default,
                    px: 1,
                    justifyContent: 'flex-start',
                    overflow: 'hidden',
                    // Single line only — flex defaults + theme can allow wrapping without these
                    whiteSpace: 'nowrap',
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    '& .MuiButton-label': {
                        display: 'block',
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left'
                    }
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        textAlign: 'left'
                    }}
                >
                    {buttonLabel}
                </Box>
            </ExtraSmallButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                container={fullscreenContainer ?? undefined}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        boxSizing: 'border-box',
                        zIndex: 99999,
                        // Width from list rows only — do not track trigger width (avoids wide popover when many expiries selected)
                        display: 'inline-block',
                        width: 'max-content',
                        maxWidth: 'calc(100vw - 32px)',
                        maxHeight: 320,
                        overflow: 'auto'
                    }
                }}
            >
                <List
                    id={`${idPrefix}-expiry-menu`}
                    dense
                    disablePadding
                    sx={{ width: 'max-content' }}
                >
                    {options.map((option) => (
                        <ListItem key={option.value} disablePadding>
                            <ListItemButton
                                dense
                                onClick={() => toggle(option.value)}
                                selected={selected.includes(option.value)}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: 0.25,
                                        pl: 0.75,
                                        pr: 0
                                    }}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={selected.includes(option.value)}
                                        tabIndex={-1}
                                        disableRipple
                                        size="small"
                                        sx={{ p: 0.25 }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={option.label}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        noWrap: true,
                                        title: option.label,
                                        sx: {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }
                                    }}
                                    sx={{
                                        m: 0,
                                        pl: 0,
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                        maxWidth: '100%'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Popover>
        </>
    )
})

export default OiAnalysisExpiriesMenu
